import { Box, type BoxProps, styled } from "@mui/material";
import { clamp } from "lodash-es";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRafLoop } from "react-use";

import images from "../../constants/images";

export type AnimationProps = Pick<BoxProps, "className" | "sx" | "style"> & {
  urls: string[];
  duration?: number;
  loop?: boolean;
  delay?: number;
  onFinish?: (animationName: string) => void;
  animationName?: string;
  dummy?: boolean;
  onFrame?: (index: number) => void;
  width: number;
  height: number;
  shadow?: boolean;
  wContainer?: number;
  sizeShadow?: number;
  xShadow?: number;
  yShadow?: number;
};

export const Animation = ({
  urls,
  sizeShadow,
  xShadow,
  yShadow,
  duration = 1000,
  loop = false,
  delay = 0,
  onFinish = () => {},
  animationName = "defaultAnimationName",
  dummy = false,
  onFrame = () => {},
  width,
  height,
  shadow,
  wContainer = 80,
  ...restProps
}: AnimationProps) => {
  const [startTime, setStartTime] = useState<number | null>(null);
  const frameTime = duration / urls.length;
  const [isFinish, setIsFinish] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const innerContainerRef = useRef<HTMLDivElement>(null);
  const size = useMemo(() => ({ width, height }), [width, height]);

  useRafLoop(() => {
    if (dummy) {
      return;
    }

    if (startTime === null) {
      return;
    }

    const deltaTime = Date.now() - startTime;
    const animationIndex = Math.floor(deltaTime / frameTime);
    const clampedAnimationIndex = clamp(animationIndex, 0, urls.length - 1);

    if (!innerContainerRef.current || !containerRef.current) {
      return;
    }

    onFrame(animationIndex);
    const position = clampedAnimationIndex * width;
    innerContainerRef.current.style.transform = `translateX(-${position}px`;

    if (animationIndex >= urls.length && loop) {
      setStartTime(Date.now() + delay);
      // onFinish(animationName);
    }

    if (animationIndex >= urls.length && !loop) {
      setIsFinish(true);
      onFinish(animationName);
    }
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (size) {
      setStartTime(Date.now() + delay);
    }
  }, [size]);

  const itemStyle = useMemo(() => {
    if (!size) {
      return {};
    }

    return {
      width: `${size.width}px`,
      height: `${size.height}px`,
    };
  }, [size]);

  if (dummy) {
    return (
      <DummyContainer>
        {urls.map((x) => (
          <Item key={x} src={x} />
        ))}
      </DummyContainer>
    );
  }

  return isFinish ? null : (
    <Container ref={containerRef} {...restProps} wcontainer={wContainer}>
      <InnerContainer ref={innerContainerRef}>
        {urls.map((x) => (
          <Item key={x} src={x} style={itemStyle} loading="lazy" />
        ))}
      </InnerContainer>
      {shadow && (
        <img
          src={images.shadown}
          alt=""
          style={{
            zIndex: -1,
            width: `${sizeShadow}px`,
            position: "absolute",
            bottom: `${yShadow}px`,
            left: `${xShadow}px`,
          }}
        />
      )}
    </Container>
  );
};

const Container = styled(Box)<{ wcontainer?: number }>`
  width: ${({ wcontainer }) => (wcontainer ? `${wcontainer}%` : "auto")};
  height: 100%;
  overflow: hidden;
`;

const InnerContainer = styled(Box)`
  display: flex;
  justify-content: start;
`;

const DummyContainer = styled(Box)`
  display: flex;
  justify-content: flex-start;
  width: 1px;
  height: 1px;
  position: absolute;
`;

const Item = styled("img")`
  flex-shrink: 0;
  padding: 0 0 0 0;
`;
